import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { authorizedFetch } from '@/utils/apiClient';
import { DOORDASH_ACCOUNT_REFUSALS, isPendingPhantomOrder } from './phantomOrderSyncPredicate';

const POLL_INTERVAL_MS = 15000;
// Slow lane for scheduled orders whose fulfillment day hasn't arrived: the
// restaurant can accept them in the DMA days early (which captures payment and
// pays out), so sweep them too — just not every 15s.
const FULL_SYNC_INTERVAL_MS = 5 * 60 * 1000;
const MAX_PENDING_SYNC_ORDERS = 25;
// Scheduling is capped at 7 days out, so any still-syncable order was created
// within ~10 days. Bounds the fetched set and avoids polling ancient rows.
const SYNC_LOOKBACK_MS = 10 * 24 * 60 * 60 * 1000;

async function fetchPendingPhantomOrders(teamId, { includeNotYetDue = false } = {}) {
  const since = new Date(Date.now() - SYNC_LOOKBACK_MS).toISOString();
  // The predicate only needs two keys out of the payload JSON blobs
  // (response_payload can carry entire Deliverect responses); selecting the
  // full columns on a 15s poll was a major disk-IO/egress burner.
  const { data, error } = await supabase
    .from('meal_orders')
    .select(`
      id,
      team_id,
      order_status,
      api_source,
      scheduled_date,
      phantom_provider_status:response_payload->phantom_provider_status,
      order_intent:request_payload->order_intent,
      restaurant:restaurants!meal_orders_restaurant_id_fkey (
        id,
        integration_type,
        provider_name
      )
    `)
    .eq('team_id', teamId)
    .in('order_status', ['pending_confirmation', 'submitted', 'confirmed'])
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(MAX_PENDING_SYNC_ORDERS);

  if (error) throw error;
  const orders = Array.isArray(data)
    ? data.map(({ phantom_provider_status, order_intent, ...rest }) => ({
        ...rest,
        response_payload:
          phantom_provider_status == null ? null : { phantom_provider_status },
        request_payload: order_intent == null ? null : { order_intent },
      }))
    : [];
  // Wrapped so filter's index argument doesn't clobber the predicate's
  // injectable `now` Date parameter.
  return orders.filter((order) => isPendingPhantomOrder(order, new Date(), { includeNotYetDue }));
}

// While Phantom is down every pending order would otherwise re-fail every 15s.
// Back off to this interval until a sync succeeds again.
const PROVIDER_OUTAGE_BACKOFF_MS = 2 * 60 * 1000;

// A refused read (the order is on a coach's own DoorDash account with no
// working sign-in here) answers the same until that coach reconnects, and a
// not-on-account refusal is a live DoorDash call on their account, which
// DoorDash locks out at ~200 per 30 minutes. So a refused order sits out the
// 15s poll and is asked again only this often. Reconnecting leaves for
// DoorDash and reloads the page, which starts over; the re-check only catches
// a reconnect made elsewhere (another tab, or the placing coach's own device
// while a teammate looks). Same cadence as the server's completion sweep.
const REFUSED_ORDER_RECHECK_MS = 10 * 60 * 1000;

// Resolves to 'synced' when the provider answered, 'unavailable' when it was
// unreachable, 'refused' for a DoorDash account refusal. The server returns
// 200 with provider_sync: 'unavailable' (or the refusal code) and the local
// status, so neither is an exception here.
async function syncOrderStatus(orderId) {
  const response = await authorizedFetch(`/api/orders/${encodeURIComponent(orderId)}/mcp/status`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.error || payload?.message || 'Failed to sync order status.');
  }

  const payload = await response.json().catch(() => null);
  if (DOORDASH_ACCOUNT_REFUSALS.has(payload?.provider_sync)) return 'refused';
  return payload?.provider_sync === 'unavailable' ? 'unavailable' : 'synced';
}

export function PhantomOrderSyncBridge() {
  const { activeTeam, user } = useAuth();
  const inFlightRef = useRef(false);
  // 0 = never, so the first tick after mount is always a full sync — catches
  // early DMA acceptances of far-out scheduled orders as soon as the app opens.
  const lastFullSyncAtRef = useRef(0);
  // 0 = provider healthy. Set while Phantom is unreachable to throttle polling.
  const outageUntilRef = useRef(0);
  // order id → when a refused order may be asked again.
  const refusedUntilRef = useRef(new Map());

  useEffect(() => {
    if (!user?.id || !activeTeam?.id) return undefined;

    let cancelled = false;

    const runSync = async () => {
      if (cancelled || inFlightRef.current) return;
      // Skip the Supabase read too — during an outage there is nothing we can
      // do with the rows, so polling them is pure disk-IO burn.
      if (Date.now() < outageUntilRef.current) return;
      inFlightRef.current = true;
      try {
        const includeNotYetDue = Date.now() - lastFullSyncAtRef.current >= FULL_SYNC_INTERVAL_MS;
        const pendingOrders = await fetchPendingPhantomOrders(activeTeam.id, { includeNotYetDue });
        if (includeNotYetDue) lastFullSyncAtRef.current = Date.now();
        const now = Date.now();
        const dueOrders = pendingOrders.filter((order) => !(refusedUntilRef.current.get(order.id) > now));
        if (!dueOrders.length) return;
        const results = await Promise.allSettled(
          dueOrders.map((order) => syncOrderStatus(order.id)),
        );
        const outcomes = results.map((result) => (result.status === 'fulfilled' ? result.value : null));
        dueOrders.forEach((order, index) => {
          if (outcomes[index] === 'refused') {
            refusedUntilRef.current.set(order.id, Date.now() + REFUSED_ORDER_RECHECK_MS);
          } else if (outcomes[index] === 'synced') {
            refusedUntilRef.current.delete(order.id);
          }
        });
        // Every order reporting the provider unreachable means Phantom is down,
        // not that these particular orders are bad. A refusal is Phantom
        // answering.
        const reachedProvider = outcomes.some((outcome) => outcome === 'synced' || outcome === 'refused');
        outageUntilRef.current = reachedProvider ? 0 : Date.now() + PROVIDER_OUTAGE_BACKOFF_MS;
      } catch (error) {
        console.error('Failed to sync pending orders', error);
      } finally {
        inFlightRef.current = false;
      }
    };

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        void runSync();
      }
    };

    void runSync();
    const intervalId = window.setInterval(() => {
      void runSync();
    }, POLL_INTERVAL_MS);

    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, [activeTeam?.id, user?.id]);

  return null;
}

export default PhantomOrderSyncBridge;
